import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { CheckCircle2, Circle, Search, UserRound, Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { viewModes } from "./view-modes";
import { ViewBadge, type ContextView } from "./view-context-picker";
import type { ChatReference } from "./reference-composer";
export interface ReferenceData {
  tasks: { id: string; title: string; status: string; priority: string; projectLabel?: string }[];
  members: { userId: string; name: string; avatarUrl?: string | null; projectLabel?: string }[];
  views: (ContextView & { projectLabel?: string })[];
}
export interface ReferencePickerHandle { handleKey(event: KeyboardEvent): boolean }
export const ReferencePicker = forwardRef<ReferencePickerHandle, {
  data?: ReferenceData;
  search: string;
  onSearch(value: string): void;
  onSelect(value: ChatReference): void;
  selectedIds?: string[];
  inline?: boolean;
}>(function ReferencePicker({
  data,
  search,
  onSearch,
  onSelect,
  selectedIds = [],
  inline = false,
}, ref) {
  const fr = useI18n().language === "fr";
  const [category, setCategory] = useState("tasks");
  const [activeIndex, setActiveIndex] = useState(0);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => setActiveIndex(0), [search, category]);
  const categories = [
    { type: "tasks", icon: CheckCircle2, fr: "Tâches", en: "Tasks" },
    { type: "people", icon: UserRound, fr: "Personnes", en: "People" },
    ...viewModes,
  ];
  const match = (name: string) =>
    name.toLocaleLowerCase().includes(search.toLocaleLowerCase());
  const items: ChatReference[] =
    category === "tasks"
      ? (data?.tasks ?? [])
          .filter((t) => match(t.title))
          .map((t) => ({
            kind: "task",
            id: t.id,
            label: t.title,
            status: t.status,
            projectLabel: t.projectLabel,
          }))
      : category === "people"
        ? (data?.members ?? [])
            .filter((m) => match(m.name))
            .map((m) => ({ kind: "member", id: m.userId, label: m.name, projectLabel: m.projectLabel }))
        : (data?.views ?? [])
            .filter((v) => v.type === category && match(v.name))
            .map((v) => ({
              kind: v.type === "DOCUMENT" ? "document" : "view",
              id: v.id,
              label: v.name,
              type: v.type,
              isShared: v.isShared,
              projectLabel: v.projectLabel,
            }));
  useEffect(() => { list.current?.querySelector(`[data-active="true"]`)?.scrollIntoView({ block: "nearest" }); }, [activeIndex]);
  useImperativeHandle(ref, () => ({ handleKey(event) {
    if (event.key === "Escape") return true;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      setActiveIndex(index => items.length ? (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length : 0);
      return true;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      const item = items[Math.min(activeIndex, items.length - 1)];
      if (item) onSelect(item);
      return true;
    }
    return false;
  } }), [items, activeIndex, onSelect]);
  return (
    <div className="flex h-full w-full min-h-0 flex-col">
      {!inline && <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
        <Search className="h-4 w-4 text-foreground-muted" />
        <input
          autoFocus
          aria-label={fr ? "Rechercher dans le projet" : "Search project"}
          className="flex-1 bg-transparent text-sm outline-none"
          placeholder={
            fr
              ? "Rechercher une tâche, une personne, une vue…"
              : "Search tasks, people, views…"
          }
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>}
      <div className={cn("flex min-h-0", inline ? "h-full max-h-full" : "h-[min(24rem,55dvh)]")}>
        <nav aria-label={fr ? "Catégories" : "Categories"} className={cn("shrink-0 space-y-1 border-r border-white/10 bg-white/[0.02] p-2 overflow-auto", inline ? "w-28 sm:w-36" : "w-36 sm:w-44")}>
          {categories.map((c) => (
            <button
              key={c.type}
              type="button"
              aria-pressed={category === c.type}
              onMouseDown={inline ? event => event.preventDefault() : undefined}
              className={cn(
                "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                category === c.type
                  ? "bg-primary/15 text-primary"
                  : "text-foreground-secondary hover:bg-white/5",
              )}
              onClick={() => setCategory(c.type)}
            >
              <c.icon className="h-4 w-4 shrink-0" />
              {fr ? c.fr : c.en}
            </button>
          ))}
        </nav>
        <div ref={list} id={inline ? "chat-inline-references" : undefined} role={inline ? "listbox" : undefined} aria-label={fr ? "Références" : "References"} className="flex-1 min-w-0 overflow-y-auto p-2">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role={inline ? "option" : undefined}
              aria-selected={inline ? index === activeIndex : undefined}
              data-active={index === activeIndex}
              onMouseDown={inline ? event => event.preventDefault() : undefined}
              onMouseEnter={() => setActiveIndex(index)}
              className={cn("flex w-full min-w-0 items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-primary/10 transition-colors", inline && index === activeIndex && "bg-primary/15 ring-1 ring-inset ring-primary/20")}
              onClick={() => onSelect(item)}
            >
              {item.kind === "task" ? (
                <>
                  {item.status === "DONE" ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <Circle className="h-4 w-4 text-amber-400" />
                  )}
                  <span className="flex-1 truncate">{item.label}</span>
                  <span className="rounded-md bg-white/5 px-2 py-0.5 text-[10px] text-foreground-muted">
                    {(
                      {
                        TODO: "À faire",
                        IN_PROGRESS: "En cours",
                        DONE: "Terminé",
                        IN_REVIEW: "En revue",
                        CANCELLED: "Annulé",
                      } as Record<string, string>
                    )[item.status ?? ""] ?? item.status}
                  </span>
                </>
              ) : item.kind === "member" ? (
                <>
                  <UserRound className="h-4 w-4 text-sky-400" />
                  <span className="truncate">{item.label}</span>
                </>
              ) : (
                <ViewBadge
                  view={{
                    id: item.id,
                    name: item.label,
                    type: item.type!,
                    isShared: !!item.isShared,
                  }}
                />
              )}
              {item.projectLabel && <span className="ml-auto max-w-24 truncate text-xs text-foreground-muted">{item.projectLabel}</span>}
              {selectedIds.includes(item.id) && (
                <Check className="ml-auto h-4 w-4 text-primary" />
              )}
            </button>
          ))}
          {!items.length && (
            <div className="px-3 py-12 text-center text-sm text-foreground-muted">
              {fr
                ? "Aucun résultat dans cette catégorie"
                : "No results in this category"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
